import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import {
  Loader2, ArrowRight, Mail, Phone, Building2, User, MessageSquare,
  Wallet, Briefcase, Sparkles, CheckCircle2,
} from "lucide-react";
import { BRAND, waLink } from "../../brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Automatización IA",
  "Implementación Respond.io",
  "Google Ads",
  "Meta Ads",
  "TikTok Ads",
  "Funnels de Venta",
  "Consultoría Estratégica",
];

const BUDGETS = [
  "$250 - $500",
  "$500 - $1,000",
  "$1,000 - $3,000",
  "$3,000 - $5,000",
  "$5,000+",
  "Enterprise",
];

const BUDGET_VALUES = {
  "$250 - $500": 375,
  "$500 - $1,000": 750,
  "$1,000 - $3,000": 2000,
  "$3,000 - $5,000": 4000,
  "$5,000+": 7500,
  "Enterprise": 15000,
};

const trackLead = (data) => {
  const value = BUDGET_VALUES[data.budget_range] || 0;
  const payload = {
    value,
    currency: "USD",
    content_name: data.service_interest || "Lead form",
    content_category: "lead",
  };
  try {
    if (typeof window !== "undefined") {
      if (typeof window.fbq === "function") window.fbq("track", "Lead", payload);
      if (typeof window.cbq === "function") window.cbq("track", "Lead", payload);
      if (typeof window.gtag === "function") window.gtag("event", "generate_lead", payload);
    }
  } catch (err) {
    console.warn("Lead tracking failed:", err);
  }
};

const empty = {
  full_name: "",
  company_name: "",
  job_title: "",
  phone: "",
  email: "",
  service_interest: SERVICES[0],
  project_need: "",
  budget_range: BUDGETS[2],
};

const detectDevice = () => {
  if (typeof window === "undefined") return "desktop";
  const ua = window.navigator.userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/iphone|android|mobile/.test(ua)) return "mobile";
  return "desktop";
};

const readUTMs = (search) => {
  const p = new URLSearchParams(search || "");
  return {
    utm_source: p.get("utm_source") || null,
    utm_medium: p.get("utm_medium") || null,
    utm_campaign: p.get("utm_campaign") || null,
    utm_content: p.get("utm_content") || null,
  };
};

export default function LeadForm() {
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const location = useLocation();

  const tracking = useMemo(
    () => ({
      ...readUTMs(typeof window !== "undefined" ? window.location.search : ""),
      landing_page: location.pathname,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      device_type: detectDevice(),
    }),
    [location.pathname]
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((d) => ({ ...d, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: null }));
  };

  const validate = () => {
    const e = {};
    if (!data.full_name.trim() || data.full_name.trim().length < 2) e.full_name = "Nombre requerido";
    if (!data.company_name.trim()) e.company_name = "Empresa requerida";
    if (!/^[+\d\s().-]{6,}$/.test(data.phone.trim())) e.phone = "Teléfono inválido";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) e.email = "Correo inválido";
    if (!data.project_need.trim() || data.project_need.trim().length < 6) e.project_need = "Cuéntame al menos una línea";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revisa los campos marcados.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...data, ...tracking });
      trackLead(data);
      setDone(true);
      toast.success("¡Recibido! Te escribiré personalmente en menos de 24h.");
      setData(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
        : "No pudimos enviar tu solicitud. Intenta por WhatsApp.";
      toast.error(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-form-section" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 bg-radial-orange opacity-60" />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left intro */}
          <div className="lg:col-span-5 reveal">
            <div className="eyebrow mb-5">Agenda una consultoría</div>
            <h2 className="font-display font-extrabold text-white text-4xl sm:text-5xl tracking-tighter leading-[1.05]">
              Hablemos de tu <span className="text-gradient">sistema de adquisición</span>.
            </h2>
            <p className="mt-6 text-zinc-400 text-base max-w-md">
              Cuéntame qué necesitas automatizar. En menos de 24 horas te escribo personalmente con un diagnóstico inicial.
            </p>

            <ul className="mt-8 space-y-3 text-sm text-zinc-300">
              {[
                "Diagnóstico inicial sin compromiso",
                "Hablas conmigo, no con un account manager",
                "Respuesta en menos de 24 horas hábiles",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#ff8a00] mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 space-y-3">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 glass rounded-xl px-4 py-3 hover:bg-white/[0.05] transition"
              >
                <Mail className="w-4 h-4 text-[#ff8a00]" />
                <span className="text-zinc-200 text-sm">{BRAND.email}</span>
              </a>
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-3 glass rounded-xl px-4 py-3 hover:bg-white/[0.05] transition"
              >
                <Phone className="w-4 h-4 text-[#25D366]" />
                <span className="text-zinc-200 text-sm">{BRAND.whatsappNumber}</span>
                <span className="ml-auto text-[10px] tracking-widest uppercase text-zinc-500">WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <div className="grad-border p-6 sm:p-10 relative overflow-hidden">
              <div className="absolute -top-40 -right-20 w-[420px] h-[420px] bg-gradient-to-br from-[#ff3b1f]/20 to-transparent rounded-full blur-3xl pointer-events-none" />
              {done ? (
                <div className="relative text-center py-10" data-testid="lead-form-success">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-brand grid place-items-center text-black shadow-lg shadow-[#ff8a00]/30">
                    <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
                  </div>
                  <h3 className="mt-5 font-display text-white text-2xl font-bold">¡Recibido!</h3>
                  <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                    Tu solicitud llegó. Te escribiré personalmente en menos de 24 horas. Si quieres adelantar, escríbeme por WhatsApp.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                    <a href={waLink()} target="_blank" rel="noreferrer" className="btn-primary">Hablar por WhatsApp</a>
                    <button onClick={() => setDone(false)} className="btn-secondary">Enviar otra solicitud</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="relative grid sm:grid-cols-2 gap-4" data-testid="lead-form" noValidate>
                  <Field icon={User} label="Nombre completo" name="full_name" value={data.full_name} onChange={onChange} placeholder="David Osorio" error={errors.full_name} />
                  <Field icon={Building2} label="Empresa" name="company_name" value={data.company_name} onChange={onChange} placeholder="Mi Empresa S.A." error={errors.company_name} />
                  <Field icon={Briefcase} label="Cargo" name="job_title" value={data.job_title} onChange={onChange} placeholder="Director de Marketing" />
                  <Field icon={Phone} label="WhatsApp" name="phone" value={data.phone} onChange={onChange} placeholder="+502 0000 0000" error={errors.phone} />
                  <Field icon={Mail} label="Correo corporativo" name="email" type="email" value={data.email} onChange={onChange} placeholder="tu@empresa.com" error={errors.email} className="sm:col-span-2" />

                  <div>
                    <Label icon={Sparkles}>Servicio de interés</Label>
                    <Select name="service_interest" value={data.service_interest} onChange={onChange} options={SERVICES} />
                  </div>

                  <div>
                    <Label icon={Wallet}>Presupuesto aproximado (USD)</Label>
                    <Select name="budget_range" value={data.budget_range} onChange={onChange} options={BUDGETS} />
                  </div>

                  <div className="sm:col-span-2">
                    <Label icon={MessageSquare}>¿Qué necesitas automatizar?</Label>
                    <textarea
                      name="project_need"
                      data-testid="lead-form-need"
                      value={data.project_need}
                      onChange={onChange}
                      rows={4}
                      placeholder="Cuéntame brevemente: canales que usas, equipo, herramientas actuales y qué quieres lograr."
                      className={`w-full bg-black/40 border rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 transition outline-none resize-none ${
                        errors.project_need
                          ? "border-red-500/60 focus:border-red-500 focus:ring-red-500/20"
                          : "border-white/10 focus:border-[#ff8a00] focus:ring-[#ff8a00]/20"
                      }`}
                    />
                    {errors.project_need && <p className="mt-1.5 text-[11px] text-red-400">{errors.project_need}</p>}
                  </div>

                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="lead-form-submit"
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {loading ? "Enviando..." : "Enviar solicitud"}
                      {!loading && <ArrowRight className="w-4 h-4" />}
                    </button>
                    <p className="text-zinc-500 text-xs max-w-sm">
                      Al enviar aceptas que David Osorio (Daos Ads) te contacte por correo o WhatsApp para tu consultoría.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <label className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-zinc-400 font-display font-semibold mb-2">
      <Icon className="w-3.5 h-3.5 text-[#ff8a00]" />
      {children}
    </label>
  );
}

function Field({ icon: Icon, label, name, value, onChange, placeholder, type = "text", error, className = "" }) {
  return (
    <div className={className}>
      <Label icon={Icon}>{label}</Label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`lead-form-${name}`}
        autoComplete="off"
        className={`w-full bg-black/40 border rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 focus:ring-2 transition outline-none ${
          error
            ? "border-red-500/60 focus:border-red-500 focus:ring-red-500/20"
            : "border-white/10 focus:border-[#ff8a00] focus:ring-[#ff8a00]/20"
        }`}
      />
      {error && <p className="mt-1.5 text-[11px] text-red-400">{error}</p>}
    </div>
  );
}

function Select({ name, value, onChange, options }) {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      data-testid={`lead-form-${name}`}
      className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:border-[#ff8a00] focus:ring-2 focus:ring-[#ff8a00]/20 transition outline-none appearance-none cursor-pointer"
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0a0a0a] text-white">
          {o}
        </option>
      ))}
    </select>
  );
}
